import * as Popover from '@radix-ui/react-popover'
import { CalendarRange, Palmtree, RotateCcw } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Tooltip } from '@/components/ui/Tooltip'
import { useDeleteAbsences, useSetAbsence } from '@/lib/time-queries'
import type { DayType, WorkdayException } from '@/lib/types'
import { useVacationPeriod } from '@/lib/useVacationPeriod'
import { cn } from '@/lib/utils'

interface DayHeaderPopoverProps {
  weekId: string
  personId?: string
  date: string
  short: string
  dayNumber: number
  isToday: boolean
  dayType: DayType
  dayLabel: string | null
  exception: WorkdayException | null
  editable: boolean
  children?: ReactNode
}

function titleFor(dayType: DayType, dayLabel: string | null): string | undefined {
  if (dayType === 'holiday') return dayLabel ?? 'Feestdag'
  if (dayType === 'holiday_worked') return dayLabel ? `${dayLabel} (toch gewerkt)` : 'Toch gewerkt op feestdag'
  if (dayType === 'vacation') return 'Vakantiedag'
  return undefined
}

export function DayHeaderPopover(props: DayHeaderPopoverProps) {
  const { weekId, personId, date, short, dayNumber, isToday, dayType, dayLabel, exception, editable, children } = props
  const [open, setOpen] = useState(false)
  const [multiOpen, setMultiOpen] = useState(false)
  const [note, setNote] = useState('')
  const setAbsence = useSetAbsence(weekId)
  const deleteAbsence = useDeleteAbsences(weekId)
  const period = useVacationPeriod(weekId, personId, date, open && dayType === 'vacation')

  const special = dayType !== 'workday' && dayType !== 'weekend'
  const title = titleFor(dayType, dayLabel)

  const label = (
    <span className="flex flex-col items-center leading-tight">
      <span className={cn('font-medium', special && 'border-b border-dotted border-ink-faint/60', dayType === 'vacation' && 'text-ink-faint')}>
        {short}
      </span>
      <span className={cn('num text-xs font-normal opacity-70', isToday && 'text-accent-700')}>{dayNumber}</span>
    </span>
  )

  if (!editable || dayType === 'weekend') {
    return (
      <Tooltip content={title}>
        <span className="flex flex-col items-center">{children ?? label}</span>
      </Tooltip>
    )
  }

  function markVacation() {
    setAbsence.mutate(
      { personId, startDate: date, kind: 'VACATION', note: note.trim() || null },
      { onSuccess: () => setOpen(false) },
    )
  }

  function markHolidayWorked() {
    setAbsence.mutate(
      { personId, startDate: date, kind: 'HOLIDAY_WORKED' },
      { onSuccess: () => setOpen(false) },
    )
  }

  function removeException() {
    if (!exception) return
    deleteAbsence.mutate([exception.id], { onSuccess: () => setOpen(false) })
  }

  function removePeriod() {
    if (period.ids.length === 0) return
    deleteAbsence.mutate(period.ids, { onSuccess: () => setOpen(false) })
  }

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            title={title}
            aria-label={`Dagacties ${short} ${dayNumber}`}
            className={cn(
              'flex flex-col items-center rounded-md px-1 py-0.5 transition-colors hover:bg-fill-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
              !children && 'w-full',
              special && !children && 'text-accent-700',
            )}
          >
            {children ?? label}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="center"
            sideOffset={6}
            className="z-40 w-64 rounded-card border border-hairline bg-surface p-3 shadow-pop animate-fade-in focus:outline-hidden"
          >
            {dayType === 'workday' ? (
              <div className="space-y-2.5">
                <div>
                  <label htmlFor={`day-note-${date}`} className="mb-1 block text-xs font-medium text-ink-soft">
                    Notitie (optioneel)
                  </label>
                  <input
                    id={`day-note-${date}`}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Bijv. vrije dag"
                    className="field"
                  />
                </div>
                <button type="button" onClick={markVacation} disabled={setAbsence.isPending} className="btn-primary h-8 w-full">
                  <Palmtree className="h-4 w-4" />
                  Markeer als vakantie
                </button>
                <button type="button" onClick={() => { setOpen(false); setMultiOpen(true) }} className="btn-secondary h-8 w-full">
                  <CalendarRange className="h-4 w-4" />
                  Meerdere dagen…
                </button>
              </div>
            ) : null}

            {dayType === 'vacation' ? (
              <div className="space-y-2.5">
                <div>
                  <p className="text-sm font-medium text-ink">Vakantiedag</p>
                  {exception?.note ? <p className="mt-0.5 text-xs text-ink-faint">{exception.note}</p> : null}
                </div>
                <button
                  type="button"
                  onClick={removeException}
                  disabled={!exception || deleteAbsence.isPending || period.loading}
                  className="btn-secondary h-8 w-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  Vakantie verwijderen
                </button>
                {period.label ? (
                  <button
                    type="button"
                    onClick={removePeriod}
                    disabled={deleteAbsence.isPending}
                    className="btn-secondary h-auto w-full flex-col items-start py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <RotateCcw className="h-4 w-4" />
                      Hele periode verwijderen
                    </span>
                    <span className="pl-[22px] text-xs font-normal text-ink-faint">
                      {`${period.label} · ${period.dates.length} dagen`}
                    </span>
                  </button>
                ) : null}
              </div>
            ) : null}

            {dayType === 'holiday' ? (
              <div className="space-y-2.5">
                <p className="text-sm font-medium text-ink">{dayLabel ?? 'Feestdag'}</p>
                <p className="text-xs text-ink-faint">Vrije feestdag. Toch gewerkt? Dan telt de dag weer mee als werkdag.</p>
                <button type="button" onClick={markHolidayWorked} disabled={setAbsence.isPending} className="btn-secondary h-8 w-full">
                  Toch gewerkt
                </button>
              </div>
            ) : null}

            {dayType === 'holiday_worked' ? (
              <div className="space-y-2.5">
                <p className="text-sm font-medium text-ink">{dayLabel ?? 'Feestdag'}</p>
                <p className="text-xs text-ink-faint">Gemarkeerd als toch gewerkt; de dag telt mee als werkdag.</p>
                <button type="button" onClick={removeException} disabled={deleteAbsence.isPending} className="btn-secondary h-8 w-full">
                  <RotateCcw className="h-4 w-4" />
                  Terugzetten naar feestdag
                </button>
              </div>
            ) : null}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {multiOpen ? (
        <MultiDayModal
          weekId={weekId}
          personId={personId}
          fromDefault={date}
          onClose={() => setMultiOpen(false)}
        />
      ) : null}
    </>
  )
}

interface MultiDayModalProps {
  weekId: string
  personId?: string
  fromDefault: string
  onClose: () => void
}

function MultiDayModal({ weekId, personId, fromDefault, onClose }: MultiDayModalProps) {
  const [from, setFrom] = useState(fromDefault)
  const [to, setTo] = useState(fromDefault)
  const [note, setNote] = useState('')
  const setAbsence = useSetAbsence(weekId)

  function submit() {
    if (!from || !to) return
    setAbsence.mutate(
      { personId, startDate: from, endDate: to, kind: 'VACATION', note: note.trim() || null },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(value) => !value && onClose()}
      title="Vakantie voor meerdere dagen"
      description="Alleen werkdagen (ma-vr, geen feestdag) worden gemarkeerd."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!from || !to || setAbsence.isPending}>
            Markeren
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="multi-from" className="mb-1 block text-sm font-medium text-ink-soft">
              Van
            </label>
            <input id="multi-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="field" />
          </div>
          <div>
            <label htmlFor="multi-to" className="mb-1 block text-sm font-medium text-ink-soft">
              Tot en met
            </label>
            <input id="multi-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} className="field" />
          </div>
        </div>
        <div>
          <label htmlFor="multi-note" className="mb-1 block text-sm font-medium text-ink-soft">
            Notitie (optioneel)
          </label>
          <input id="multi-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Bijv. zomervakantie" className="field" />
        </div>
      </div>
    </Modal>
  )
}
